/**
 * Silensia Recovery — Homepage and Programme-page interactions (2026-08-24,
 * revised 2026-08-25, extended 2026-08-28 to also run on the dedicated
 * Programme page). Progressive, dependency-free vanilla JS, no frontend
 * framework, no third-party dependency, no polling, no continuous
 * animation loop. Every initializer fails safely when its target elements
 * are absent, so this file loads harmlessly on pages that don't render the
 * markup it targets (enqueued from sections/silensia-residential-environment.liquid
 * on the Homepage and sections/silensia-programme-content.liquid on
 * Programme; each initializer stays defensive regardless of which page
 * loaded it, rather than branching on page type). Three responsibilities:
 *
 *  1. Residential environment gallery (2-5 images): the same proven
 *     offsetLeft-based, IntersectionObserver-driven technique already
 *     established by assets/silensia-support-carousel.js, kept as a
 *     separate controller (distinct data-gallery-* hooks) since this
 *     gallery's desktop behavior — a static editorial grid, not a second
 *     carousel — genuinely differs once past 990px. Unchanged by the
 *     2026-08-25 pass.
 *  2. Floating Contact CTA visibility (snippets/silensia-floating-contact-cta.liquid,
 *     2026-08-25, replaces the removed desktop rail/mobile bar controller
 *     below): hidden while the page's own opening CTA is in view, hidden
 *     again once the closing zone enters view, visible in between. Also
 *     hides while the shared page-index navigation panel is open (revised
 *     2026-08-25 -- see assets/silensia-page-index.js), listening for its
 *     silensia:nav-panel-open/close events rather than importing that file
 *     directly, so the two stay loosely coupled. Extended 2026-08-27 with a
 *     fourth input, a midpoint inline CTA, observed with a positive bottom
 *     rootMargin so the floating CTA has already hidden by the time the
 *     inline CTA is genuinely on screen -- the two are never simultaneously
 *     visible. Generalised 2026-08-28 to read whichever of the Homepage's
 *     or Programme's own targets actually exist on the current page
 *     (#silensia-hero-cta / #silensia-programme-opening-cta as the opening
 *     input; #homepage-contact / #silensia-page-closing as the closing
 *     input; #silensia-journey-cta-wrap / #silensia-programme-break-cta-wrap
 *     as the midpoint input) rather than duplicating this whole function
 *     for a second page. One applyState() remains the single writer of
 *     data-silensia-floating-state; every input is a boolean that function
 *     reads, never a second place that sets the attribute directly.
 *     Fifth input added 2026-08-28 (global floating-CTA/disclosure overlap
 *     fix, not scoped to any one section): silensia:disclosure-open/close,
 *     dispatched sitewide by assets/silensia-disclosure.js whenever any
 *     disclosure anywhere on the page opens or closes -- opening one never
 *     scrolls the page, so the fixed pill could otherwise sit directly over
 *     newly-revealed content regardless of which section it belongs to.
 *  3. Restrained editorial scroll reveal (2026-08-26, initChapterReveals()):
 *     a curated set of high-level elements (chapter headings, the primary
 *     image or gallery group, the Principles/Programme groups, the Journey
 *     diagram) fade and rise slightly the first time each enters view, once
 *     only, never replaying on scroll-up. Progressive enhancement only --
 *     .silensia-reveal (snippets/silensia-brand-styles.liquid) renders fully
 *     visible on its own; .is-pending is added only after a target element
 *     has genuinely been found and a working IntersectionObserver has been
 *     created, so a JavaScript failure or missing IntersectionObserver
 *     support never leaves anything invisible.
 *
 * Respects prefers-reduced-motion (the gallery's smooth-scroll navigation
 * falls back to an instant jump, matching the Support carousel exactly).
 * Never calls preventDefault on genuine navigation. Reinitializes safely on
 * Shopify Theme Editor's shopify:section:load event for the sections this
 * file's targets live in, so adding/removing/reordering blocks in the
 * Residential environment section keeps working without a full page reload.
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Mouse-drag-to-scroll (corrective pass), identical technique and
  // reasoning to assets/silensia-support-carousel.js's own initPointerDrag()
  // -- duplicated rather than shared across the two files, matching this
  // codebase's existing pattern of two independent, structurally similar
  // carousel controllers (see initGallery()'s own file-level comment on
  // why the two were never merged into one shared controller).
  function initPointerDrag(viewport) {
    var isDown = false;
    var startX = 0;
    var startScrollLeft = 0;

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true;
      startX = e.clientX;
      startScrollLeft = viewport.scrollLeft;
      viewport.style.scrollSnapType = 'none';
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture(e.pointerId);
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      viewport.scrollLeft = startScrollLeft - (e.clientX - startX);
    });

    function endDrag(e) {
      if (!isDown) return;
      isDown = false;
      viewport.style.scrollSnapType = '';
      viewport.classList.remove('is-dragging');
      if (e.pointerId !== undefined && viewport.hasPointerCapture(e.pointerId)) {
        viewport.releasePointerCapture(e.pointerId);
      }
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
  }

  // ---------------------------------------------------------------------
  // Residential environment gallery
  // ---------------------------------------------------------------------

  function initGallery(root) {
    var viewport = root.querySelector('[data-gallery-viewport]');
    var track = root.querySelector('[data-gallery-track]');
    var controls = root.querySelector('[data-silensia-gallery]');
    if (!viewport || !track || !controls) return;

    var prevBtn = controls.querySelector('[data-gallery-prev]');
    var nextBtn = controls.querySelector('[data-gallery-next]');
    var currentEl = controls.querySelector('[data-gallery-current]');

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-gallery-slide]'));
    if (slides.length < 2) return;

    initPointerDrag(viewport);

    var activeIndex = 0;

    function pad2(n) {
      return n < 10 ? '0' + n : String(n);
    }

    function updateControls() {
      if (prevBtn) prevBtn.disabled = activeIndex === 0;
      if (nextBtn) nextBtn.disabled = activeIndex === slides.length - 1;
      if (currentEl) currentEl.textContent = pad2(activeIndex + 1);
    }

    function setActiveIndex(index) {
      if (index < 0 || index > slides.length - 1 || index === activeIndex) return;
      activeIndex = index;
      updateControls();
    }

    function goToIndex(index) {
      var clamped = Math.max(0, Math.min(index, slides.length - 1));
      var target = slides[clamped];
      // Bug fix (corrective pass), same root cause and fix as
      // assets/silensia-support-carousel.js's own goToIndex(): offsetLeft
      // is relative to the offsetParent, not this scroll viewport (static
      // position, never the slide's real offsetParent), so it silently
      // clamped "go back" navigation to the viewport's own max scrollLeft
      // instead of returning to an earlier slide. Delta-based positioning
      // is correct regardless of the offsetParent chain.
      var delta = target.getBoundingClientRect().left - viewport.getBoundingClientRect().left;
      viewport.scrollTo({
        left: viewport.scrollLeft + delta,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
      setActiveIndex(clamped);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goToIndex(activeIndex - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goToIndex(activeIndex + 1);
      });
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              var index = slides.indexOf(entry.target);
              if (index !== -1) setActiveIndex(index);
            }
          });
        },
        { root: viewport, threshold: [0.6] }
      );
      slides.forEach(function (slide) {
        observer.observe(slide);
      });
    }

    updateControls();
  }

  function initGalleries() {
    var galleries = document.querySelectorAll('.silensia-environment__inner');
    for (var i = 0; i < galleries.length; i++) {
      initGallery(galleries[i]);
    }
  }

  // ---------------------------------------------------------------------
  // Floating Contact CTA visibility (2026-08-25, replaces the removed
  // desktop rail / mobile bar controller; extended 2026-08-27 with a
  // fourth state input so the floating CTA and the Journey section's own
  // inline CTA are never visible at the same time)
  // ---------------------------------------------------------------------

  function initFloatingCta() {
    var root = document.querySelector('[data-silensia-floating-cta]');
    if (!root || !('IntersectionObserver' in window)) return;

    // The link itself, not just its wrapper: pointer-events:none (CSS,
    // unchanged) already stops mouse/touch activation while hidden, but a
    // keyboard user tabbing through the page could still land on the anchor
    // with no visible focus target. aria-hidden and tabindex are toggled
    // alongside the same data-silensia-floating-state attribute the CSS
    // already reads, from this one applyState() -- never a second place
    // deciding visibility.
    var link = root.querySelector('.silensia-floating-cta__link');

    // Corrective pass (2026-08-25, second): suppression is now driven
    // entirely by an explicit data-silensia-floating-cta-suppress attribute
    // on the wrappers that actually contain a real, visible conversion
    // point -- the Hero/opening CTA, the Homepage's own midpoint Obsidian
    // band and Journey CTA, Programme's own Obsidian break and closing
    // invitation, the Homepage and Contact forms, and the footer's own CTA
    // (sections/silensia-hero.liquid, silensia-programme-opening.liquid,
    // silensia-midpoint-cta.liquid, silensia-arrival-journey.liquid,
    // silensia-programme-content.liquid, silensia-page-closing.liquid,
    // silensia-homepage-contact.liquid, silensia-enquiry.liquid,
    // silensia-footer.liquid). This replaces the previous hardcoded id
    // lookup, which had grown a genuine bug: Programme's Coordination
    // diagram (.silensia-coordination-diagram, an informational diagram
    // with no CTA of its own) had been added to the same suppression list
    // as a plain "obstruction to avoid," which -- because
    // IntersectionObserver reports a target as intersecting for as long as
    // any part of it is on screen, and the diagram is a large element
    // spanning most of "How the six areas are coordinated" -- suppressed
    // the floating CTA for nearly the section's entire scroll range, not
    // just the one specific scroll position where the two visually
    // overlapped. The diagram is deliberately not given the suppress
    // attribute: it is not a CTA, and this section has none.
    var suppressors = Array.prototype.slice.call(
      document.querySelectorAll('[data-silensia-floating-cta-suppress]')
    );

    var suppressorVisible = false;
    var navPanelOpen = false;

    // Corrective pass (28 August 2026): a fixed-position pill can end up
    // covering an open disclosure's own revealed content anywhere on the
    // page, not just a specific section -- opening a disclosure never
    // scrolls the page (assets/silensia-disclosure.js's own openExpanded()
    // comment), so whatever was at the bottom of the viewport stays there,
    // now potentially including newly-visible disclosure content. Rather
    // than a per-section padding workaround, the floating CTA hides itself
    // globally, sitewide, for as long as any disclosure anywhere is open --
    // the same silensia:disclosure-open/close events every disclosure
    // already dispatches sitewide (assets/silensia-disclosure.js), read the
    // same way silensia:nav-panel-open/close already are two lines below.
    // Seeded synchronously from the real DOM state (not assumed false) so
    // a disclosure already open via hash-navigation at page load -- which
    // can run its own init() before this listener attaches, since this
    // script loads later than assets/silensia-disclosure.js -- is still
    // accounted for from this function's very first applyState() call.
    var disclosureOpen = document.querySelectorAll('.silensia-disclosure[open]').length > 0;

    // Corrective pass: the previous implementation only faded the inner
    // link's own opacity and toggled its aria-hidden/tabindex -- verified
    // live that this left the wrapper (and, mid-transition, the link
    // itself) technically present, hit-testable, and still in the
    // accessibility tree by any means other than aria-hidden, not truly
    // "absent" while a modal dialog is on top of it. `inert` (set on the
    // wrapper, which contains only this one anchor) is the single native
    // mechanism that removes an entire subtree from hit-testing, tab
    // order, and the accessibility tree all at once, so it now does that
    // job directly; aria-hidden/tabindex stay as-is underneath it purely
    // as a fallback for the small remaining set of browsers without
    // `inert` support (assigning an unrecognised IDL property is a silent
    // no-op there, never a thrown error). CSS (below) now also carries the
    // fade through to a real `visibility: hidden` once it completes, and
    // `pointer-events: none` on the wrapper itself, not just the link.
    function applyState() {
      var visible = !suppressorVisible && !navPanelOpen && !disclosureOpen;
      root.setAttribute('data-silensia-floating-state', visible ? 'visible' : 'hidden');
      root.inert = !visible;
      if (link) {
        if (visible) {
          link.removeAttribute('aria-hidden');
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('aria-hidden', 'true');
          link.setAttribute('tabindex', '-1');
        }
      }
    }

    // Hidden by default until the observer's first real callback corrects
    // it -- the opening CTA (Hero or Programme's own) is always the first
    // suppressor in view at page load, so this avoids a flash of the pill
    // before that first callback fires, without needing a separate assumed
    // -visible flag for one specific target.
    root.setAttribute('data-silensia-floating-state', 'hidden');
    root.inert = true;
    if (link) {
      link.setAttribute('aria-hidden', 'true');
      link.setAttribute('tabindex', '-1');
    }

    document.addEventListener('silensia:nav-panel-open', function () {
      navPanelOpen = true;
      applyState();
    });
    document.addEventListener('silensia:nav-panel-close', function () {
      navPanelOpen = false;
      applyState();
    });
    window.addEventListener('silensia:disclosure-open', function () {
      disclosureOpen = true;
      applyState();
    });
    window.addEventListener('silensia:disclosure-close', function () {
      disclosureOpen = false;
      applyState();
    });

    if (!suppressors.length) {
      // No real conversion point found on this page at all -- fail open,
      // matching the sitewide "without JavaScript the CTA stays visible"
      // safe default (snippets/silensia-floating-contact-cta.liquid),
      // rather than leaving the pill permanently hidden.
      applyState();
      return;
    }

    // A positive bottom rootMargin extends the intersection root past the
    // true viewport edge, so a suppressor counts as "visible" while it is
    // still below the fold -- specifically while it is within roughly the
    // floating CTA's own footprint of the bottom edge. This gives the
    // floating CTA's ~300ms hide transition a genuine head start, so it has
    // already faded (and lost pointer-events) before the real CTA is
    // actually on screen, rather than the two overlapping for a frame. All
    // suppressors share one observer instance; each target's own
    // intersecting state is tracked separately (an IntersectionObserver
    // callback only reports entries whose state actually changed, not every
    // observed target every time, so OR-ing only the entries in one batch
    // would be wrong once more than one target exists) and
    // suppressorVisible is recomputed as "any tracked target currently
    // intersecting" on every callback.
    var suppressorStates = new Map();
    suppressors.forEach(function (target) {
      suppressorStates.set(target, false);
    });

    var suppressorObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          suppressorStates.set(entry.target, entry.isIntersecting);
        });
        suppressorVisible = Array.from(suppressorStates.values()).some(Boolean);
        applyState();
      },
      { threshold: 0, rootMargin: '0px 0px 150px 0px' }
    );
    suppressors.forEach(function (target) {
      suppressorObserver.observe(target);
    });
  }

  // ---------------------------------------------------------------------
  // Hero background video (2026-08-25, DESIGN.md's "The Hero Video Rule")
  // ---------------------------------------------------------------------

  function initHeroVideo() {
    var video = document.querySelector('[data-silensia-hero-video]');
    if (!video) return;

    // No autoplay HTML attribute exists on the element at all (see
    // sections/silensia-hero.liquid) -- reduced-motion visitors simply never
    // have play() called, so the poster stays the only visible frame, no
    // flash of motion before this check runs.
    if (prefersReducedMotion()) return;

    // Mobile image selected (2026-08-25): the video element itself is
    // already display:none below 990px via CSS, and its own preload
    // attribute is "none" rather than "metadata" (sections/silensia-hero.liquid)
    // -- this check is what stops play() from ever being called on it at
    // that width, which is what would otherwise trigger the browser to
    // start fetching the video regardless of its visibility.
    if (
      video.hasAttribute('data-silensia-hero-video-has-mobile-image') &&
      window.matchMedia &&
      window.matchMedia('(max-width: 989px)').matches
    ) {
      return;
    }

    video.addEventListener('playing', function () {
      video.setAttribute('data-silensia-hero-video-ready', '');
    });

    // Retry policy (live bug fix, 26 August 2026, revised same day after
    // deeper diagnosis): a bare, unretried play() call on page load was
    // found live to silently fail. Live event-trace debugging (temporary
    // instrumentation, since removed) identified the exact rejection: not
    // a generic autoplay block, but
    //   AbortError: "The play() request was interrupted because
    //   video-only background media was paused to save power."
    // -- Chrome's own Video Power Saver feature, which can pause a muted,
    // video-only autoplaying element it judges isn't being actively
    // watched, and -- confirmed live -- can re-pause it again later even
    // after a retry has it playing normally (observed: video played
    // correctly for several seconds, then stopped again with no further
    // code-side trigger). This is real browser resource-saving behavior,
    // not a defect in this markup; it is also the reason a plain "retry
    // once on first interaction" policy proved insufficient once the
    // video had already been playing and got re-paused with no further
    // interaction in between. Three retry paths now cover it: (1) once
    // real data is available (the original race-condition retry), (2) on
    // the visitor's first genuine interaction, and (3) on every
    // subsequent 'pause' event the video fires on its own while the tab
    // is visible and the video has already made real progress
    // (currentTime > 0 rules out fighting a still-legitimate initial
    // block). attemptPlay() itself no-ops whenever the video is already
    // playing, so none of these paths can race each other into a
    // play/pause loop. The poster image remains the visible fallback for
    // however long playback stays paused; nothing about the page's
    // functionality depends on the video ever starting.
    function attemptPlay() {
      if (!video.paused) return;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          // Still blocked -- the remaining retry paths stay armed; no
          // error surfaced, no polling loop.
        });
      }
    }

    attemptPlay();

    video.addEventListener('loadeddata', attemptPlay, { once: true });

    video.addEventListener('pause', function () {
      if (document.visibilityState === 'visible' && video.currentTime > 0) {
        attemptPlay();
      }
    });

    var retryEvents = ['pointerdown', 'touchstart', 'keydown'];
    function retryOnFirstInteraction() {
      attemptPlay();
      retryEvents.forEach(function (evt) {
        window.removeEventListener(evt, retryOnFirstInteraction);
      });
    }
    retryEvents.forEach(function (evt) {
      window.addEventListener(evt, retryOnFirstInteraction, { once: true, passive: true });
    });
  }

  // ---------------------------------------------------------------------
  // Restrained editorial scroll reveal (2026-08-26)
  // ---------------------------------------------------------------------

  function initChapterReveals() {
    var targets = document.querySelectorAll('.silensia-reveal, .silensia-arrival-journey__list.has-line');
    if (!targets.length) return; // Nothing found -- base CSS already renders everything visible.
    if (!('IntersectionObserver' in window)) return; // No pending class is ever added; content stays visible.
    if (prefersReducedMotion()) return; // Renders the completed state immediately, no animation.

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('is-pending');
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Runs once; never replays on scroll-up.
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    for (var i = 0; i < targets.length; i++) {
      var target = targets[i];
      if (target.classList.contains('is-visible')) continue; // Theme Editor re-render safety.
      target.classList.add('is-pending');
      observer.observe(target);
    }

    // Fail-safe (2 September 2026). Everything above is progressive
    // enhancement EXCEPT one case it never covered: the observer is
    // created successfully and then simply never fires. That happens for
    // real -- IntersectionObserver callbacks do not run while a document
    // is not being rendered (document.hidden), so a page opened in a
    // background tab, restored from the back/forward cache, or loaded in
    // an embedded/offscreen view can finish loading with every reveal
    // target still at opacity 0. Confirmed live in exactly that state
    // while building this pass.
    //
    // On a clinical site, content that is permanently invisible is not an
    // acceptable failure mode for a decorative animation. So: anything
    // still pending after a grace period is revealed unconditionally.
    // On a normally-rendered page the observer always wins long before
    // this fires, and the timer is cleared once nothing is left pending.
    var failSafe = window.setTimeout(function () {
      var stuck = document.querySelectorAll('.silensia-reveal.is-pending, .is-pending');
      for (var j = 0; j < stuck.length; j++) {
        stuck[j].classList.remove('is-pending');
        stuck[j].classList.add('is-visible');
      }
    }, 3000);

    // If the document was hidden at load, re-check as soon as it is shown:
    // the observer will normally fire on its own at that point, but this
    // makes the recovery immediate rather than waiting out the timer.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(failSafe);
      failSafe = window.setTimeout(function () {
        var stuck = document.querySelectorAll('.is-pending');
        for (var j = 0; j < stuck.length; j++) {
          stuck[j].classList.remove('is-pending');
          stuck[j].classList.add('is-visible');
        }
      }, 1200);
    });
  }

  function init() {
    initGalleries();
    initFloatingCta();
    initHeroVideo();
    initChapterReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor: re-run when the Residential environment section (block
  // add/remove/reorder) or any section carrying one of the floating CTA's
  // own observer targets reloads, so editing stays live without a full
  // page reload -- covers both the Homepage's and Programme's own ids.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.querySelector('.silensia-environment__inner')) initGalleries();
    if (
      target.querySelector('#silensia-hero-cta') ||
      target.querySelector('#homepage-contact') ||
      target.querySelector('#silensia-journey-cta-wrap') ||
      target.querySelector('#silensia-programme-opening-cta') ||
      target.querySelector('#silensia-page-closing') ||
      target.querySelector('#silensia-programme-break-cta-wrap')
    ) {
      initFloatingCta();
    }
    if (target.querySelector('.silensia-reveal, .silensia-arrival-journey__list.has-line')) {
      initChapterReveals();
    }
  });
})();
